import React from 'react';
import { Box } from '@mui/material';
import { OrderStatus } from '../../enums/OrderStatus.ts';

/**
 * On-site orders: the customer orders now (ASAP, never a slot), pays in the
 * app, and either eats at the restaurant (DINE_IN, backend migration 115) or
 * collects at the counter (PICKUP). No courier, no delivery fee. The server
 * sends `orderType` and, for both on-site types, the `dineIn` object:
 * `{ arrivalAt, arrivalLocal, partySize, label }` (for pickup arrivalAt is when
 * the customer will collect, and partySize is null).
 */
export type OrderType = 'DELIVERY' | 'DINE_IN' | 'PICKUP';

export type DineInInfo = {
    /** When the customer expects to arrive / collect, UTC with a Z. */
    arrivalAt?: string | null;
    /** The same time on the restaurant clock, "19:10" (newer backend). */
    arrivalLocal?: string | null;
    /** Dine in only; null for pickup. */
    partySize?: number | null;
    /** The server's own wording, e.g. "PICKUP · collecting ~19:10" (newer backend). */
    label?: string | null;
};

export const isDineIn = (orderType?: string | null): boolean => orderType === 'DINE_IN';

/** The enum's own name SELF_PICK_UP is accepted by the server too; read both. */
export const isPickup = (orderType?: string | null): boolean => orderType === 'PICKUP' || orderType === 'SELF_PICK_UP';

/** Dine in or pickup: no courier, no Wolt, no hand-over; the restaurant completes it. */
export const isOnSite = (orderType?: string | null): boolean => isDineIn(orderType) || isPickup(orderType);

/** Restaurant-local clock time; every on-site venue is in Cyprus today. */
export const RESTAURANT_TIME_ZONE = 'Asia/Nicosia';

export const arrivalClock = (arrivalAt?: string | null): string | null => {
    if (!arrivalAt) return null;
    // Accept both "...Z" and a bare UTC wall clock.
    const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(arrivalAt) ? arrivalAt : `${arrivalAt}Z`;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: RESTAURANT_TIME_ZONE });
};

/** The restaurant-clock time: the server's arrivalLocal when sent, else computed. */
export const onSiteClock = (dineIn?: DineInInfo | null): string | null => {
    const local = dineIn?.arrivalLocal?.trim();
    if (local) return local.length > 5 && /^\d\d:\d\d/.test(local) ? local.slice(0, 5) : local;
    return arrivalClock(dineIn?.arrivalAt);
};

/** "arriving ~19:10" / "collecting ~19:10" (null when there is no time). */
export const onSiteWhen = (orderType?: string | null, dineIn?: DineInInfo | null): string | null => {
    const clock = onSiteClock(dineIn);
    if (!clock) return null;
    return `${isPickup(orderType) ? 'collecting' : 'arriving'} ~${clock}`;
};

/**
 * "DINE IN · arriving ~19:10 · party of 2" / "PICKUP · collecting ~19:10":
 * the server's `label` when it sends one, else built the same way (older
 * backend, dine-in only).
 */
export const onSiteLabel = (orderType?: string | null, dineIn?: DineInInfo | null): string => {
    const label = dineIn?.label?.trim();
    if (label) return label;
    const pickup = isPickup(orderType);
    const parts = [pickup ? 'PICKUP' : 'DINE IN'];
    const when = onSiteWhen(orderType, dineIn);
    if (when) parts.push(when);
    if (!pickup && dineIn?.partySize) parts.push(`party of ${dineIn.partySize}`);
    return parts.join(' · ');
};

/** Short type name for tables: "Dine in" / "Pickup" / "Delivery". */
export const orderTypeName = (orderType?: string | null): string =>
    isPickup(orderType) ? 'Pickup' : isDineIn(orderType) ? 'Dine in' : 'Delivery';

/** What a finished on-site order reads instead of "Delivered": "Served" / "Collected". */
export const onSiteDoneLabel = (orderType?: string | null): string => (isPickup(orderType) ? 'Collected' : 'Served');

/** Badge colours: dine in purple, pickup teal. [background, text, light background] */
export const ON_SITE_COLOURS = {
    DINE_IN: { solid: '#5B21B6', dark: '#4C1D95', light: '#EDE9FE' },
    PICKUP: { solid: '#0F766E', dark: '#115E59', light: '#CCFBF1' },
} as const;

export const onSiteColours = (orderType?: string | null) =>
    isPickup(orderType) ? ON_SITE_COLOURS.PICKUP : ON_SITE_COLOURS.DINE_IN;

/** Status wording: an on-site order's DELIVERED is "Served" / "Collected"; ready = "Ready at the counter". */
export const statusLabel = (status?: string | null, orderType?: string | null): string => {
    const key = (status ?? '').toUpperCase();
    if (isOnSite(orderType)) {
        if (key === 'DELIVERED') return onSiteDoneLabel(orderType);
        if (key === 'READY_FOR_PICKUP') return 'Ready at the counter';
    }
    return (OrderStatus as Record<string, string>)[key] || status || '—';
};

export const OnSiteBadge: React.FC<{ orderType?: string | null; dineIn?: DineInInfo | null; size?: 'small' | 'large' }> = ({ orderType, dineIn, size = 'small' }) => (
    <Box
        component="span"
        sx={{
            display: 'inline-block',
            px: size === 'large' ? 1.25 : 0.75,
            py: size === 'large' ? 0.5 : 0.25,
            borderRadius: '4px',
            backgroundColor: onSiteColours(orderType).solid,
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: size === 'large' ? '14px' : '11px',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
        }}
    >
        {onSiteLabel(orderType, dineIn)}
    </Box>
);
